import { IncreaseNominalValueDTO, IssueNewSharesDTO, Transaction } from "@/types/Transactions";
import { RowAction, TableColumn } from "@/components-v2/DataTable";
import { compact, isObject } from "lodash";
import { Box } from "@/components-v2/Box";
import { Icon } from "@/components-v2/Icon";
import { TransactionType } from "@/types/enums/TransactionType";
import { Tag } from "@/components-v2/Tag";
import { isBefore } from "@/utils/Dates";
import { InfoButton } from "@/components-v2/InfoButton";
import { IconName } from "@/components-v2/iconMapping";
import { isInitialTransactionType } from "@/types/const/Transactions";
import { DropdownMenuItems } from "@/components-v2/Dropdown";

export type AutoImportSectionProps = {
  onConfirmAutoImportIsRead: () => void;
  isLoading: boolean;
  onboardingDate: string;
  country: string;
};

export function prepareTransactionLogTableColumns({
  changeTransactionDate,
  firstNotRegisteredIssueDate,
  hideUpdated,
}: {
  changeTransactionDate?: (transaction: Transaction) => void;
  hideUpdated?: boolean;
  firstNotRegisteredIssueDate?: string;
}): Array<TableColumn<Transaction>> {
  return compact([
    {
      name: "icon",
      label: "",
      align: "left",
      width: "10px",
      getValue: (logItem) => [
        "icon",
        { name: transactionTypeIconMapping[logItem.transactionType] },
      ],
    },
    {
      name: "transactionType",
      label: { key: "transactions.transaction-type" },
      align: "left",
      getValue: (logItem, _, { t }) => {
        const transactionType =
          logItem.transactionType === "ISSUE"
            ? "transactions.summary.issue-new-shares"
            : logItem.transactionType;
        return t(transactionType);
      },
    },
    {
      name: "nameFrom",
      label: { key: "transactions.description" },
      align: "left",
      getSortValue: (logItem, _, { lang }) =>
        logItem.descriptions.find((d) => d.language === lang)?.text,
      getValue: (logItem, _, { lang }) => {
        const isAfterNotRegisteredIssue =
          firstNotRegisteredIssueDate &&
          isBefore(firstNotRegisteredIssueDate, logItem.transactionDate);
        return (
          <Box justifyContent="space-between">
            <div>{logItem.descriptions.find((d) => d.language === lang)?.text}</div>
            {isNotRegisteredIssue(logItem) ? (
              <Box alignItems={"center"}>
                <Tag
                  color={"warning"}
                  label={{ key: "transactions.not-registered-in-br" }}
                  onClick={
                    changeTransactionDate
                      ? (e) => {
                          e.stopPropagation();
                          changeTransactionDate(logItem);
                        }
                      : undefined
                  }
                  tooltip={{ key: "transactions.not-registered-in-br.tooltip" }}
                />
                <InfoButton infoText={{ key: "transactions.not-registered-in-br.info" }} />
              </Box>
            ) : (
              isAfterNotRegisteredIssue && (
                <Box>
                  <Tag color={"neutral"} label={{ key: "transactions.not-reported" }} />
                  <InfoButton infoText={{ key: "transactions.not-reported.info" }} />
                </Box>
              )
            )}
            {logItem.attachments?.length > 0 && <Icon name={"paperclip"} />}
          </Box>
        );
      },
    },
    !hideUpdated && {
      name: "updated",
      label: { key: "transactions.last-updated" },
      align: "right",
      getSortValue: (logItem) => logItem.updatedDate,
      getValue: (logItem, _, { f }) => f.formatDate(logItem.updatedDate),
    },
    {
      name: "transactionDate",
      label: { key: "transactions.dateFull" },
      align: "right",
      getSortValue: (logItem) => logItem.transactionDate,
      getValue: (logItem, _, { f }) => f.formatDate(logItem.transactionDate),
    },
  ]);
}

export function prepareTransactionLogTableRowActions(
  transaction: Transaction,
  {
    deleteTransaction,
    changeTransactionDate,
    changeNominalValue,
    editTransaction,
    downloadContractNote,
  }: {
    deleteTransaction?: (transaction: Transaction) => void;
    changeTransactionDate?: (transaction: Transaction) => void;
    changeNominalValue?: (transaction: Transaction) => void;
    editTransaction?: (transaction: Transaction) => void;
    downloadContractNote?: (transaction: Transaction) => void;
  },
): RowAction[] {
  const menuItems: DropdownMenuItems = compact([
    changeNominalValue &&
      isInitialTransactionType(transaction.transactionType) && {
        type: "item",
        label: { key: "transactions.change-nominal-value" },
        onSelect: () => changeNominalValue(transaction),
        icon: "edit",
      },
    editTransaction &&
      supportsEdit(transaction) && {
        type: "item",
        label: { key: "transactions.edit" },
        onSelect: () => editTransaction(transaction),
        icon: "edit",
      },
    changeTransactionDate && {
      type: "item",
      label: isNotRegisteredIssue(transaction)
        ? { key: "transactions.enter-registration-date" }
        : { key: "transactions.change-date" },
      onSelect: () => changeTransactionDate(transaction),
      icon: "calendar",
    },
    deleteTransaction && {
      type: "item",
      label: { key: "transactions.delete" },
      onSelect: () => deleteTransaction(transaction),
      icon: "trash",
      color: "error",
    },
  ]);

  return compact([
    downloadContractNote &&
      transaction.transactionType === "BUY_SELL" && [
        "icon",
        {
          icon: "download",
          tooltip: { key: "shareholders.downloadContractNotes" },
          onClick: () => downloadContractNote(transaction),
        },
      ],
    menuItems.length > 0 && ["context-menu", menuItems],
  ]);
}

export function isNotRegisteredIssue(logItem: Transaction) {
  if (logItem.transactionType === "ISSUE") {
    const data = logItem.payload as IssueNewSharesDTO;
    return data.isPending === true;
  } else if (logItem.transactionType === "INCREASE_NOMINAL_VALUE") {
    const data = logItem.payload as IncreaseNominalValueDTO;
    return data.isPending === true;
  }
  return false;
}

export const transactionTypeIconMapping: Record<TransactionType, IconName> = {
  [TransactionType.AUTOIMPORT]: "sparkle",
  [TransactionType.INCORPORATION]: "champagne",
  [TransactionType.ISSUE]: "file-plus",
  [TransactionType.BUY_SELL]: "money",
  [TransactionType.GIFT]: "parachute",
  [TransactionType.INHERITANCE]: "arrow-bend-double-up-right",
  [TransactionType.OTHER_TRANSFER]: "arrows-left-right",
  [TransactionType.SPLIT]: "arrows-out-line-horizontal",
  [TransactionType.REVERSE_SPLIT]: "arrows-in-line-horizontal",
  [TransactionType.INCREASE_NOMINAL_VALUE]: "arrow-fat-line-up",
  [TransactionType.CANCELLATION_OF_SHARES]: "file-minus",
  [TransactionType.REDUCTION_NOMINAL_VALUE]: "arrow-fat-line-down",
  [TransactionType.PAY_DIVIDEND]: "potted-plant",
  [TransactionType.GROUP_CONTRIBUTION]: "briefcase",
  [TransactionType.CONVERSION_OF_SHARES]: "arrow-u-up-right",
  [TransactionType.CONVERSION_FROM_ISIN]: "code-block",
  [TransactionType.SHAREHOLDING_PER_DATE]: "calendar-check",
  [TransactionType.OWNERS_PER_DATE]: "calendar-check",
  [TransactionType.LIQUIDATION]: "trash",
  [TransactionType.MERGER]: "unite",
  [TransactionType.DEMERGER]: "arrows-split",
  [TransactionType.COMPANY_STATUS_CHANGE]: "building",
};

export const optionExerciseCount = (transaction: Transaction): number => {
  const payload = transaction.payload;
  return isObject(payload) &&
    "optionExerciseIds" in payload &&
    Array.isArray(payload.optionExerciseIds)
    ? payload.optionExerciseIds.length
    : 0;
};

export const supportsEdit = (transaction: Transaction): boolean =>
  transactionTypeSupportingEdit.includes(transaction.transactionType) &&
  optionExerciseCount(transaction) === 0;

export const transactionTypeSupportingEdit: TransactionType[] = [
  TransactionType.AUTOIMPORT,
  TransactionType.GROUP_CONTRIBUTION,
  TransactionType.SPLIT,
  TransactionType.REVERSE_SPLIT,
  TransactionType.BUY_SELL,
  TransactionType.GIFT,
  TransactionType.INHERITANCE,
  TransactionType.OTHER_TRANSFER,
  TransactionType.PAY_DIVIDEND,
  TransactionType.MERGER,
  TransactionType.ISSUE,
  TransactionType.INCORPORATION,
  TransactionType.INCREASE_NOMINAL_VALUE,
  TransactionType.REDUCTION_NOMINAL_VALUE,
  TransactionType.LIQUIDATION,
  TransactionType.CANCELLATION_OF_SHARES,
  TransactionType.CONVERSION_FROM_ISIN,
  TransactionType.CONVERSION_OF_SHARES,
  TransactionType.DEMERGER,
  TransactionType.SHAREHOLDING_PER_DATE,
  TransactionType.OWNERS_PER_DATE,
];
